"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { FiliumLogo } from "@/components/brand/FiliumLogo";
import {
  getCurrentUserOrNull,
  signOut,
  type AuthUser,
} from "@/lib/auth/browser";
import { runAction } from "@/lib/actions/client";
import {
  claimMySeatAction,
  getSubscriptionStatusAction,
  listMyPendingInvitesAction,
  openBillingPortalAction,
} from "@/lib/billing/actions";
import { SeatsConsole } from "@/components/billing/SeatsConsole";
import {
  getOrCreateMyOrgAction,
  listMyOrgsAction,
  type OrganizationChoice,
} from "@/lib/orgs/actions";

type Subscription = {
  org_id: string;
  product_key: string;
  status: string;
  units_total: number;
  unit_kind: string;
  current_period_end: string | null;
} | null;

type LicenseFetch =
  | { state: "idle" | "loading" }
  | { state: "ready"; token: string; expires: number }
  | { state: "none"; reason: string }
  | { state: "error"; message: string };

type FabricInvitation =
  | { state: "idle" | "loading" }
  | { state: "ready"; uri: string; expiresAt: string }
  | { state: "error"; message: string };

type VenturiDownload =
  | { state: "loading" }
  | {
      state: "ready";
      version: string;
      revision: string;
      target: string;
      sha256: string;
      qualification: { status: string; scope: string };
      downloadUrl: string;
      installerUrl: string;
    }
  | { state: "unavailable"; message: string };

/**
 * Account page — session-aware, rendered fully client-side per the
 * deployment contract (authed data never depends on SSR seeing a fresh
 * cookie; the browser client is the session owner).
 */
export default function AccountPage() {
  const router = useRouter();
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined);
  const [sub, setSub] = useState<Subscription | undefined>(undefined);
  const [license, setLicense] = useState<LicenseFetch>({ state: "idle" });
  const [copied, setCopied] = useState(false);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationChoice[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [fabricRoute, setFabricRoute] = useState("");
  const [fabricInvitation, setFabricInvitation] = useState<FabricInvitation>({
    state: "idle",
  });
  const [fabricCopied, setFabricCopied] = useState(false);
  const [venturiDownload, setVenturiDownload] = useState<VenturiDownload>({
    state: "loading",
  });

  const loadSubscription = useCallback(async () => {
    const r = await runAction(getSubscriptionStatusAction, {});
    const value = r.ok ? (r.value as Subscription) : null;
    setSub(value);
    if (value === null) {
      // No claimed seat — a teammate invite may be waiting for this email.
      const invites = await runAction(listMyPendingInvitesAction, {});
      setPendingInvites(invites.ok ? (invites.value as unknown[]).length : 0);
    }
  }, []);

  const loadOrganizations = useCallback(async () => {
    // Signup creates the user identity; the first account visit creates the
    // user's personal organization if it does not exist yet.
    const ensured = await runAction(getOrCreateMyOrgAction, {});
    if (!ensured.ok) return;
    const listed = await runAction(listMyOrgsAction, {});
    if (!listed.ok) return;
    const choices = listed.value as OrganizationChoice[];
    setOrganizations(choices);
    setSelectedOrganization((current) => current || choices[0]?.id || "");
  }, []);

  const loadVenturiDownload = useCallback(async () => {
    try {
      const response = await fetch("/api/native/venturi/manifest");
      const body = (await response.json()) as {
        success?: boolean;
        data?: {
          version: string;
          revision: string;
          target: string;
          sha256: string;
          qualification: { status: string; scope: string };
          download_url: string;
          installer: { download_url: string };
        };
        error?: { message?: string };
      };
      if (!response.ok || !body.success || !body.data) {
        setVenturiDownload({
          state: "unavailable",
          message: body.error?.message ?? "No Venturi build is available.",
        });
        return;
      }
      setVenturiDownload({
        state: "ready",
        version: body.data.version,
        revision: body.data.revision,
        target: body.data.target,
        sha256: body.data.sha256,
        qualification: body.data.qualification,
        downloadUrl: body.data.download_url,
        installerUrl: body.data.installer.download_url,
      });
    } catch {
      setVenturiDownload({
        state: "unavailable",
        message: "Could not load Venturi builds.",
      });
    }
  }, []);

  useEffect(() => {
    getCurrentUserOrNull().then((u) => {
      setUser(u);
      if (!u) {
        router.replace("/login?next=/account");
        return;
      }
      void loadSubscription();
      void loadOrganizations();
      void loadVenturiDownload();
    });
  }, [router, loadSubscription, loadOrganizations, loadVenturiDownload]);

  async function createFabricInvitation() {
    if (!selectedOrganization) {
      setFabricInvitation({
        state: "error",
        message: "Select an organization first.",
      });
      return;
    }
    setFabricInvitation({ state: "loading" });
    try {
      const endpoint = fabricRoute.trim();
      const response = await fetch("/api/fabric/invitations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_id: selectedOrganization,
          route: endpoint ? { kind: "cloudflare_tunnel", endpoint } : null,
          capabilities: endpoint ? ["venturi.search"] : [],
          labels: { enrollment: "account" },
        }),
      });
      const body = (await response.json()) as {
        success?: boolean;
        data?: { invitation_uri?: string; expires_at?: string };
        error?: { message?: string };
      };
      if (
        !response.ok ||
        !body.success ||
        !body.data?.invitation_uri ||
        !body.data.expires_at
      ) {
        setFabricInvitation({
          state: "error",
          message: body.error?.message ?? "Invitation creation failed.",
        });
        return;
      }
      setFabricInvitation({
        state: "ready",
        uri: body.data.invitation_uri,
        expiresAt: body.data.expires_at,
      });
    } catch {
      setFabricInvitation({ state: "error", message: "Network error." });
    }
  }

  async function claimSeat() {
    setClaiming(true);
    try {
      const r = await runAction(claimMySeatAction, {});
      if (r.ok) {
        setPendingInvites(0);
        await loadSubscription();
      }
    } finally {
      setClaiming(false);
    }
  }

  const fetchLicense = useCallback(async () => {
    setLicense({ state: "loading" });
    try {
      const res = await fetch("/api/license");
      const body = (await res.json()) as {
        success: boolean;
        data?: { token: string | null; reason?: string; expires?: number };
        error?: { message: string };
      };
      if (!body.success) {
        setLicense({
          state: "error",
          message: body.error?.message ?? "Request failed",
        });
      } else if (!body.data?.token) {
        setLicense({ state: "none", reason: body.data?.reason ?? "unknown" });
      } else {
        setLicense({
          state: "ready",
          token: body.data.token,
          expires: body.data.expires ?? 0,
        });
      }
    } catch {
      setLicense({ state: "error", message: "Network error" });
    }
  }, []);

  async function openPortal() {
    const r = await runAction(openBillingPortalAction, {});
    if (r.ok) window.location.assign((r.value as { url: string }).url);
  }

  async function handleSignOut() {
    await signOut();
    router.replace("/");
  }

  if (user === undefined) {
    return (
      <main className="relative z-10 flex min-h-dvh items-center justify-center">
        <p className="text-[var(--muted)]">Loading…</p>
      </main>
    );
  }
  if (user === null) return null; // redirecting

  const panel =
    "glass relative overflow-hidden rounded-2xl p-8 shadow-[0_10px_50px_-12px_rgba(0,0,0,0.7)]";
  const button =
    "rounded-full border border-white/15 bg-[var(--bg)]/45 px-5 py-2 text-sm font-medium text-white backdrop-blur-md transition hover:border-white/25 hover:bg-[var(--bg)]/35";

  return (
    <div className="relative z-10 mx-auto min-h-dvh max-w-3xl px-6">
      <header className="flex items-center justify-between py-5">
        <Link href="/">
          <FiliumLogo size={42} variant="graph" />
        </Link>
        <button
          type="button"
          onClick={handleSignOut}
          className="text-sm text-[var(--muted)] transition hover:text-white"
        >
          Sign out
        </button>
      </header>

      <main className="space-y-5 py-10">
        <section className={panel}>
          <h1 className="display text-2xl">Account</h1>
          <p className="mt-2 text-[var(--muted)]">{user.email}</p>
        </section>

        <section className={panel}>
          <h2 className="text-lg font-semibold text-white">Subscription</h2>
          {sub === undefined ? (
            <p className="mt-3 text-[var(--muted)]">Loading…</p>
          ) : sub === null ? (
            <>
              <p className="mt-3 text-[var(--muted)]">
                No active subscription.
              </p>
              {pendingInvites > 0 ? (
                <>
                  <p className="mt-2 text-sm text-cyan-200">
                    A team seat is waiting for your email.
                  </p>
                  <button
                    type="button"
                    onClick={claimSeat}
                    disabled={claiming}
                    className={`mt-4 ${button} disabled:opacity-50`}
                  >
                    {claiming ? "Claiming…" : "Claim your seat"}
                  </button>
                </>
              ) : (
                <Link href="/pricing" className={`mt-5 inline-block ${button}`}>
                  See pricing
                </Link>
              )}
            </>
          ) : (
            <>
              <p className="mt-3 text-white">
                {sub.product_key}{" "}
                <span className="ml-2 rounded-full border border-cyan-400/40 bg-white/5 px-2.5 py-0.5 text-xs text-cyan-300">
                  {sub.status}
                </span>
              </p>
              {sub.current_period_end && (
                <p className="mt-1 text-sm text-[var(--muted)]">
                  Renews {new Date(sub.current_period_end).toLocaleDateString()}
                </p>
              )}
              <button
                type="button"
                onClick={openPortal}
                className={`mt-5 ${button}`}
              >
                Manage billing
              </button>
            </>
          )}
        </section>

        {sub && sub.unit_kind === "seat" && (
          <section className={panel}>
            <h2 className="text-lg font-semibold text-white">Team seats</h2>
            <SeatsConsole orgId={sub.org_id} seatCap={sub.units_total} />
          </section>
        )}

        <section className={panel}>
          <h2 className="text-lg font-semibold text-white">Venturi CLI</h2>
          <p className="mt-2 text-sm text-[var(--muted)]">
            Download the current Linux command-line candidate. The download is
            tied to your signed-in account and its SHA-256 checksum is published
            before installation.
          </p>
          {venturiDownload.state === "ready" ? (
            <>
              <p className="mt-4 text-sm text-white">
                {venturiDownload.version} · {venturiDownload.target} ·{" "}
                {venturiDownload.qualification.status}
              </p>
              <p className="mt-1 break-all font-mono text-xs text-[var(--muted)]">
                revision {venturiDownload.revision}
                <br />
                sha256 {venturiDownload.sha256}
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a className={button} href={venturiDownload.installerUrl}>
                  Download installer
                </a>
                <a className={button} href={venturiDownload.downloadUrl}>
                  Download Venturi
                </a>
              </div>
            </>
          ) : venturiDownload.state === "unavailable" ? (
            <p className="mt-4 text-sm text-rose-300">
              {venturiDownload.message}
            </p>
          ) : (
            <p className="mt-4 text-sm text-[var(--muted)]">
              Loading candidate…
            </p>
          )}
        </section>

        <section className={panel}>
          <h2 className="text-lg font-semibold text-white">
            Venturi installations
          </h2>
          <p className="mt-2 text-sm text-[var(--muted)]">
            Generate a one-time invitation, then give it to the local Venturi
            MCP on the machine you are enrolling. The installation creates and
            keeps its own signing key.
          </p>

          <label className="mt-5 block text-sm text-[var(--muted)]">
            Fabric
            <select
              value={selectedOrganization}
              onChange={(event) => setSelectedOrganization(event.target.value)}
              className="mt-2 w-full rounded-xl border border-white/15 bg-[var(--bg)]/70 px-4 py-2 text-white"
            >
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </label>

          <label className="mt-4 block text-sm text-[var(--muted)]">
            Public Fabric RPC URL (optional)
            <input
              type="url"
              value={fabricRoute}
              onChange={(event) => setFabricRoute(event.target.value)}
              placeholder="https://venturi.example.com/fabric/v1/rpc"
              className="mt-2 w-full rounded-xl border border-white/15 bg-[var(--bg)]/70 px-4 py-2 text-white placeholder:text-white/30"
            />
          </label>
          <p className="mt-2 text-xs text-[var(--muted)]">
            Leave blank for a client-only installation. A URL publishes this
            installation as a searchable target; transport never replaces
            Venturi authorization.
          </p>

          <button
            type="button"
            onClick={createFabricInvitation}
            disabled={
              !selectedOrganization || fabricInvitation.state === "loading"
            }
            className={`mt-5 ${button} disabled:opacity-50`}
          >
            {fabricInvitation.state === "loading"
              ? "Generating…"
              : "Generate invitation"}
          </button>

          {fabricInvitation.state === "ready" && (
            <div className="mt-4">
              <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-all rounded-xl border border-white/10 bg-black/30 p-4 text-xs text-cyan-100">
                {fabricInvitation.uri}
              </pre>
              <p className="mt-2 text-xs text-[var(--muted)]">
                Expires {new Date(fabricInvitation.expiresAt).toLocaleString()}{" "}
                and can be used once.
              </p>
              <button
                type="button"
                className={`mt-3 ${button}`}
                onClick={() => {
                  navigator.clipboard
                    .writeText(fabricInvitation.uri)
                    .then(() => {
                      setFabricCopied(true);
                      setTimeout(() => setFabricCopied(false), 2000);
                    });
                }}
              >
                {fabricCopied ? "Copied" : "Copy invitation"}
              </button>
            </div>
          )}
          {fabricInvitation.state === "error" && (
            <p className="mt-4 text-sm text-rose-300">
              {fabricInvitation.message}
            </p>
          )}
        </section>

        <section className={panel}>
          <h2 className="text-lg font-semibold text-white">Offline license</h2>
          <p className="mt-2 text-sm text-[var(--muted)]">
            Your signed license token. Set it as{" "}
            <code className="text-white">FILIUM_LICENSE</code> where your
            Sentinel deployment runs. Re-download after renewals.
          </p>
          {license.state === "ready" ? (
            <>
              <pre className="mt-4 max-h-32 overflow-auto whitespace-pre-wrap break-all rounded-xl border border-white/10 bg-black/30 p-4 text-xs text-cyan-100">
                {license.token}
              </pre>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  className={button}
                  onClick={() => {
                    navigator.clipboard.writeText(license.token).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    });
                  }}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
                <a
                  className={button}
                  download="filium-license.txt"
                  href={`data:text/plain;charset=utf-8,${encodeURIComponent(license.token)}`}
                >
                  Download
                </a>
              </div>
            </>
          ) : license.state === "none" ? (
            <p className="mt-4 text-sm text-[var(--muted)]">
              {license.reason === "no-active-subscription"
                ? "No active subscription — a license is minted once you subscribe."
                : license.reason === "multiple-orgs"
                  ? "You hold seats in several organizations — org selection is coming; contact support meanwhile."
                  : `No license available (${license.reason}).`}
            </p>
          ) : license.state === "error" ? (
            <p className="mt-4 text-sm text-rose-300">{license.message}</p>
          ) : (
            <button
              type="button"
              onClick={fetchLicense}
              disabled={license.state === "loading"}
              className={`mt-5 ${button} disabled:opacity-50`}
            >
              {license.state === "loading" ? "Minting…" : "Get license token"}
            </button>
          )}
        </section>
      </main>
    </div>
  );
}
